import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';

interface TabNavItemProps {
  to?: string;
  label: string;
  exact?: boolean;
  isActive?: boolean;
  isUpdated?: boolean;
  onClick?: () => void;
  className?: string;
}

const TabNavItemStyles = css`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: auto;
  height: ${toRem(33)};
  padding: 0 ${toRem(theme.space.xl)};
  border-radius: ${toRem(5.5)};
  background-color: transparent;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  text-decoration: none;
  color: ${theme.color.white};
  transition: background-color ${theme.transition.button.fast};

  &:hover {
    background-color: rgba(0, 0, 0, 0.055);
  }

  &:last-child {
    margin-right: 0;
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
    font-weight: ${theme.font.weight.medium};
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    width: 45%;
    padding: 0 ${toRem(theme.space.xs)};
    text-align: center;
    flex-grow: 1;
  }
`;

const ActiveStyles = css`
  background-color: ${theme.color.secondary[300]};
  color: ${theme.color.neutral[900]};

  &:hover {
    background-color: ${theme.color.secondary[400]};
  }
`;

const TabNavButton = styled.button.attrs({
  type: 'button',
})<Partial<TabNavItemProps>>`
  ${TabNavItemStyles}

  ${({ isActive }) => isActive && ActiveStyles}
`;

const TabNavLink = styled(NavLink)`
  ${TabNavItemStyles}

  &.active {
    ${ActiveStyles}
  }
`;

const Attention = styled.span.attrs({
  'aria-hidden': 'true',
})<Partial<TabNavItemProps>>`
  display: flex;
  flex-grow: 0;
  width: ${toRem(6)};
  height: ${toRem(6)};
  margin-right: ${toRem(theme.space.sm)};
  margin-left: ${toRem(-theme.space.xs)};
  border-radius: 50%;
  background-color: ${theme.color.secondary[400]};

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${theme.color.neutral[900]};
    `}
`;

const TabNavItem: React.FC<TabNavItemProps> = ({ to, label, exact, isActive, isUpdated, onClick, className, ...rest }) => {
  const content = (
    <>
      {isUpdated && <Attention isActive={isActive} />}
      <span>{label}</span>
    </>
  );

  const ariaLabel = isUpdated ? `${label} has updates. Click to view ${label}.` : `Click to view ${label}.`;

  return to ? (
    <TabNavLink to={to} onClick={onClick} className={className} aria-label={ariaLabel} exact={exact} {...rest}>
      {content}
    </TabNavLink>
  ) : (
    <TabNavButton isActive={isActive} onClick={onClick} className={className} aria-label={ariaLabel} {...rest}>
      {content}
    </TabNavButton>
  );
};

export default TabNavItem;
